import React, { useCallback, useEffect, useState, useRef, Component } from 'react';
import { getCount, onCountChange } from '../../lib/setupCounter';
import { ZapIcon, CreditCardIcon, BarChart3Icon, LayersIcon, RepeatIcon, ShoppingCartIcon, SmartphoneIcon, CodeIcon, GlobeIcon, HelpCircleIcon, CalendarIcon, ClockIcon, TicketIcon, PenLineIcon, CalculatorIcon, ReceiptIcon, UsersIcon, ShieldCheckIcon } from 'lucide-react';
import { CopilotMessage } from './CopilotMessage';
import { CopilotQuickReplies } from './CopilotQuickReplies';
import { CopilotInput } from './CopilotInput';
import { CopilotSummary } from './CopilotSummary';
interface Message {
  id: string;
  role: 'ai' | 'user';
  content: string;
  quickReplies?: {
    label: string;
    icon?: ComponentType<{
      className?: string;
    }>;
  }[];
  showSummary?: boolean;
  isTyping?: boolean;
}
interface CollectedData {
  buildingType: string;
  chargeModel: string;
  pricingTiers: string;
  products: string;
  billingFrequency: string;
  freeTrial: string;
  coupons: string;
  tax: string;
  invoices: string;
  billingManager: string;
  techStack: string;
  stripeAccount: string;
}
const INITIAL_SUGGESTIONS = [{
  label: 'Set up SaaS subscriptions',
  icon: RepeatIcon
}, {
  label: 'Configure one-time payments',
  icon: CreditCardIcon
}, {
  label: 'Add usage-based billing',
  icon: BarChart3Icon
}, {
  label: 'Explore pricing models',
  icon: LayersIcon
}];
const STEP_CONFIG = [{
  aiMessage: "Hi! I'm PayKit — I'll help you set up your payments stack in minutes. What type of product are you building?",
  quickReplies: [{
    label: 'SaaS App',
    icon: ZapIcon
  }, {
    label: 'Marketplace',
    icon: GlobeIcon
  }, {
    label: 'Mobile App',
    icon: SmartphoneIcon
  }, {
    label: 'API / Platform',
    icon: CodeIcon
  }, {
    label: 'E-commerce',
    icon: ShoppingCartIcon
  }, {
    label: 'Other',
    icon: HelpCircleIcon
  }],
  dataKey: 'buildingType' as const
}, {
  aiMessage: 'Great choice! How will you charge your customers?',
  quickReplies: [{
    label: 'Subscription',
    icon: RepeatIcon
  }, {
    label: 'One-time',
    icon: CreditCardIcon
  }, {
    label: 'Usage-based',
    icon: BarChart3Icon
  }, {
    label: 'Freemium',
    icon: LayersIcon
  }, {
    label: 'Multiple',
    icon: LayersIcon
  }],
  dataKey: 'chargeModel' as const
}, {
  aiMessage: 'What pricing tiers do you need? You can describe them or pick a common setup.',
  quickReplies: [{
    label: 'Starter + Pro + Enterprise',
    icon: LayersIcon
  }, {
    label: 'Free + Pro',
    icon: ZapIcon
  }, {
    label: 'Single tier',
    icon: CreditCardIcon
  }, {
    label: 'Let me type...',
    icon: PenLineIcon
  }],
  dataKey: 'pricingTiers' as const
}, {
  aiMessage: 'What products and prices do you want to offer? (e.g. Pro £15 monthly + one-time setup fee of £10)',
  quickReplies: [{
    label: 'Starter $9/mo, Pro $29/mo',
    icon: LayersIcon
  }, {
    label: 'Standard $49 one-time',
    icon: CreditCardIcon
  }, {
    label: 'Let me type...',
    icon: PenLineIcon
  }],
  dataKey: 'products' as const
}, {
  aiMessage: 'What billing frequency works best?',
  quickReplies: [{
    label: 'Monthly only',
    icon: CalendarIcon
  }, {
    label: 'Annual only',
    icon: CalendarIcon
  }, {
    label: 'Both monthly & annual',
    icon: CalendarIcon
  }],
  dataKey: 'billingFrequency' as const
}, {
  aiMessage: 'Will you offer free trials to new users?',
  quickReplies: [{
    label: 'Yes, 7-day trial',
    icon: ClockIcon
  }, {
    label: 'Yes, 14-day trial',
    icon: ClockIcon
  }, {
    label: 'Yes, 30-day trial',
    icon: ClockIcon
  }, {
    label: 'No free trials',
    icon: ClockIcon
  }],
  dataKey: 'freeTrial' as const
}, {
  aiMessage: 'Last question — will you use coupon or promo codes?',
  quickReplies: [{
    label: "Yes, I'll use coupons",
    icon: TicketIcon
  }, {
    label: 'No coupons needed',
    icon: TicketIcon
  }],
  dataKey: 'coupons' as const
}, {
  aiMessage: 'Do you need to collect sales tax, VAT, or GST from customers?',
  quickReplies: [{
    label: 'Yes, enable tax collection',
    icon: CalculatorIcon
  }, {
    label: 'No, skip tax for now',
    icon: CalculatorIcon
  }],
  dataKey: 'tax' as const
}, {
  aiMessage: 'Should we generate PDF invoices for each payment?',
  quickReplies: [{
    label: 'Yes, generate invoices',
    icon: ReceiptIcon
  }, {
    label: 'No invoices needed',
    icon: ReceiptIcon
  }],
  dataKey: 'invoices' as const
}, {
  aiMessage: 'Who will manage billing changes like plan upgrades and cancellations?',
  quickReplies: [{
    label: 'Customers self-serve',
    icon: UsersIcon
  }, {
    label: 'Admin manages',
    icon: ShieldCheckIcon
  }, {
    label: 'Both',
    icon: LayersIcon
  }],
  dataKey: 'billingManager' as const
}, {
  aiMessage: 'What tech stack are you using? This helps us tailor your implementation pack.',
  quickReplies: [{
    label: 'Next.js + Node.js + PostgreSQL',
    icon: CodeIcon
  }, {
    label: 'React + Node.js + PostgreSQL',
    icon: CodeIcon
  }, {
    label: 'Vue + Python + MySQL',
    icon: CodeIcon
  }, {
    label: 'Other / Let me type...',
    icon: PenLineIcon
  }],
  dataKey: 'techStack' as const
}, {
  aiMessage: 'Last question — do you already have a Stripe account?',
  quickReplies: [{
    label: 'Yes, I have one',
    icon: CreditCardIcon
  }, {
    label: 'No, I need to create one',
    icon: CreditCardIcon
  }, {
    label: 'Not sure',
    icon: HelpCircleIcon
  }],
  dataKey: 'stripeAccount' as const
}];
let messageIdCounter = 0;
function nextId(): string {
  messageIdCounter += 1;
  return `msg-${messageIdCounter}`;
}
export function PayKitCopilot() {
  const [messages, setMessages] = useState<Message[]>([]);
  const [currentStep, setCurrentStep] = useState(-1); // -1 = not started
  const [collectedData, setCollectedData] = useState<CollectedData>({
    buildingType: '',
    chargeModel: '',
    pricingTiers: '',
    products: '',
    billingFrequency: '',
    freeTrial: '',
    coupons: '',
    tax: '',
    invoices: '',
    billingManager: '',
    techStack: '',
    stripeAccount: ''
  });
  const [isTyping, setIsTyping] = useState(false);
  const [showCustomInput, setShowCustomInput] = useState(false);
  const [conversationStarted, setConversationStarted] = useState(false);
  const [setupCount, setSetupCount] = useState(getCount);
  const [displayCount, setDisplayCount] = useState(getCount);
  const chatEndRef = useRef<HTMLDivElement>(null);
  const chatContainerRef = useRef<HTMLDivElement>(null);
  // Listen for real-time count changes from other tabs
  useEffect(() => {
    return onCountChange(newCount => {
      setSetupCount(newCount);
    });
  }, []);
  // Animate the displayed number when setupCount changes
  useEffect(() => {
    if (displayCount === setupCount) return;
    const diff = setupCount - displayCount;
    const steps = Math.min(Math.abs(diff), 20);
    const stepTime = Math.max(30, 600 / steps);
    let current = displayCount;
    const increment = diff > 0 ? 1 : -1;
    const timer = setInterval(() => {
      current += increment;
      setDisplayCount(current);
      if (current === setupCount) clearInterval(timer);
    }, stepTime);
    return () => clearInterval(timer);
  }, [setupCount, displayCount]);
  const scrollToBottom = useCallback(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTo({
        top: chatContainerRef.current.scrollHeight,
        behavior: 'smooth'
      });
    }
  }, []);
  useEffect(() => {
    scrollToBottom();
  }, [messages, isTyping, scrollToBottom]);
  const addAiMessage = useCallback((step: number) => {
    if (step >= STEP_CONFIG.length) {
      // Summary step
      setMessages(prev => [...prev, {
        id: nextId(),
        role: 'ai',
        content: "Perfect! Here's your payment setup summary:",
        showSummary: true
      }]);
      setIsTyping(false);
      return;
    }
    const config = STEP_CONFIG[step];
    setMessages(prev => [...prev, {
      id: nextId(),
      role: 'ai',
      content: config.aiMessage,
      quickReplies: config.quickReplies.map(qr => ({
        label: qr.label,
        icon: qr.icon
      }))
    }]);
    setIsTyping(false);
  }, []);
  const startConversation = useCallback((initialMessage?: string) => {
    setConversationStarted(true);
    if (initialMessage) {
      setMessages([{
        id: nextId(),
        role: 'user',
        content: initialMessage
      }]);
      setIsTyping(true);
      setTimeout(() => {
        // Map suggestion to a product type
        let buildingType = '';
        if (initialMessage.toLowerCase().includes('saas') || initialMessage.toLowerCase().includes('subscription')) {
          buildingType = 'SaaS App';
        } else if (initialMessage.toLowerCase().includes('one-time')) {
          buildingType = 'E-commerce';
        } else if (initialMessage.toLowerCase().includes('usage')) {
          buildingType = 'API / Platform';
        }
        if (buildingType) {
          setCollectedData(prev => ({
            ...prev,
            buildingType
          }));
          setCurrentStep(1);
          setMessages(prev => [...prev, {
            id: nextId(),
            role: 'ai',
            content: `Got it — I'll set you up for a ${buildingType.toLowerCase()} with ${initialMessage.toLowerCase().includes('subscription') ? 'subscription' : initialMessage.toLowerCase().includes('one-time') ? 'one-time' : 'usage-based'} billing. How will you charge your customers?`,
            quickReplies: STEP_CONFIG[1].quickReplies.map(qr => ({
              label: qr.label,
              icon: qr.icon
            }))
          }]);
          setIsTyping(false);
        } else {
          setCurrentStep(0);
          addAiMessage(0);
        }
      }, 1200);
    } else {
      setCurrentStep(0);
      addAiMessage(0);
    }
  }, [addAiMessage]);
  const handleQuickReply = useCallback((label: string) => {
    if (isTyping) return;
    const step = currentStep;
    if (step < 0 || step >= STEP_CONFIG.length) return;
    // Handle "Let me type..." option
    if (label.includes('Let me type')) {
      setShowCustomInput(true);
      // Remove quick replies from last message
      setMessages(prev => prev.map((msg, i) => i === prev.length - 1 ? {
        ...msg,
        quickReplies: undefined
      } : msg));
      return;
    }
    const config = STEP_CONFIG[step];
    // Add user message
    setMessages(prev => {
      const updated = prev.map((msg, i) => i === prev.length - 1 ? {
        ...msg,
        quickReplies: undefined
      } : msg);
      return [...updated, {
        id: nextId(),
        role: 'user' as const,
        content: label
      }];
    });
    // Store data
    setCollectedData(prev => ({
      ...prev,
      [config.dataKey]: label
    }));
    setShowCustomInput(false);
    // Show typing, then next message
    setIsTyping(true);
    const nextStep = step + 1;
    setCurrentStep(nextStep);
    setTimeout(() => {
      addAiMessage(nextStep);
    }, 1200);
  }, [currentStep, isTyping, addAiMessage]);
  const handleTextSend = useCallback((text: string) => {
    if (isTyping) return;
    if (!conversationStarted) {
      startConversation(text);
      return;
    }
    const step = currentStep;
    if (step < 0 || step >= STEP_CONFIG.length) return;
    const config = STEP_CONFIG[step];
    // Remove quick replies from last AI message
    setMessages(prev => {
      const updated = prev.map((msg, i) => i === prev.length - 1 ? {
        ...msg,
        quickReplies: undefined
      } : msg);
      return [...updated, {
        id: nextId(),
        role: 'user' as const,
        content: text
      }];
    });
    setCollectedData(prev => ({
      ...prev,
      [config.dataKey]: text
    }));
    setShowCustomInput(false);
    setIsTyping(true);
    const nextStep = step + 1;
    setCurrentStep(nextStep);
    setTimeout(() => {
      addAiMessage(nextStep);
    }, 1200);
  }, [currentStep, isTyping, conversationStarted, startConversation, addAiMessage]);
  const handleSuggestionClick = useCallback((label: string) => {
    startConversation(label);
  }, [startConversation]);
  const lastAiMessageWithReplies = messages.findLast(m => m.role === 'ai' && m.quickReplies && m.quickReplies.length > 0);
  return <div className="w-full max-w-3xl mx-auto" data-id="element-119">
      {/* Heading */}
      <h1 className="text-4xl md:text-5xl font-bold tracking-tight text-foreground mb-8 text-center" data-id="element-120">
        {conversationStarted ? 'Setting up your payments' : 'What can I help you build?'}
      </h1>

      {/* Chat Container */}
      <div className="flex flex-col" data-id="element-121">
        {conversationStarted ? <>
            {/* Messages Area */}
            <div className="border border-border rounded-2xl bg-card shadow-lg overflow-hidden" data-id="element-122">
              <div ref={chatContainerRef} className="max-h-[420px] overflow-y-auto p-5 space-y-5" data-id="element-123">
                {messages.map(msg => <div key={msg.id} data-id="element-124">
                    <CopilotMessage role={msg.role} content={msg.content} isTyping={msg.isTyping} data-id="element-125">
                      {msg.showSummary && <CopilotSummary data={collectedData} data-id="element-126" />}
                      {msg.quickReplies && msg.quickReplies.length > 0 && msg.id === lastAiMessageWithReplies?.id && <CopilotQuickReplies options={msg.quickReplies} onSelect={handleQuickReply} disabled={isTyping} data-id="element-127" />}
                    </CopilotMessage>
                  </div>)}

                {isTyping && <CopilotMessage role="ai" content="" isTyping data-id="element-128" />}

                <div ref={chatEndRef} data-id="element-129" />
              </div>

              {/* Input Area */}
              {currentStep < STEP_CONFIG.length && <div className="border-t border-border p-4" data-id="element-130">
                  <CopilotInput onSend={handleTextSend} disabled={isTyping} placeholder={showCustomInput ? 'Describe your pricing tiers...' : 'Type a message or pick an option above...'} showDecorations={false} data-id="element-131" />
                </div>}
            </div>
          </> : <>
            {/* Initial State - Input + Suggestions + Stats */}
            <div className="mb-8" data-id="element-132">
              <CopilotInput onSend={handleTextSend} placeholder="Tell PayKit about your product..." showDecorations data-id="element-133" />
            </div>

            {/* Suggestion Pills */}
            <div className="flex flex-wrap justify-center gap-3 mb-10" data-id="element-134">
              {INITIAL_SUGGESTIONS.map(suggestion => {
            const Icon = suggestion.icon;
            return <button key={suggestion.label} onClick={() => handleSuggestionClick(suggestion.label)} className="inline-flex items-center gap-2.5 border border-border rounded-full px-5 py-2.5 text-sm font-medium text-foreground bg-card hover:bg-muted/50 hover:border-primary/50 hover:shadow-sm transition-all focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 shadow-sm" data-id="element-135">
                    <Icon className="w-4 h-4 text-muted-foreground" data-id="element-136" />
                    {suggestion.label}
                  </button>;
          })}
            </div>

            {/* Stats Cards */}
            <div className="flex justify-center gap-4" data-id="element-137">
              <div className="border border-border rounded-xl px-8 py-5 bg-card text-center min-w-[160px] shadow-sm" data-id="element-138">
                <div className="text-sm font-medium text-muted-foreground mb-1" data-id="element-139">
                  Setups completed
                </div>
                <div className="text-3xl font-bold text-foreground" data-id="element-140">
                  {displayCount.toLocaleString()}
                </div>
              </div>
              <div className="border border-border rounded-xl px-8 py-5 bg-card text-center min-w-[160px] shadow-sm" data-id="element-141">
                <div className="text-sm font-medium text-muted-foreground mb-1" data-id="element-142">
                  Avg time saved
                </div>
                <div className="text-3xl font-bold text-foreground" data-id="element-143">
                  4 hours
                </div>
              </div>
            </div>
          </>}
      </div>
    </div>;
}